/**
 * DealHeaderCard — Compact deal info bar shown below the chat header
 * when a deal is active. Shows address, stage badge, and completeness %.
 * Clickable to open deal detail panel.
 */

import DealStageBadge from "@/components/shared/DealStageBadge";

interface DealHeaderCardProps {
  address?: string;
  parcelId?: string;
  stage: string;
  completeness: number;
  title?: string;
  onClick?: () => void;
}

function ChevronRight() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function DealHeaderCard({
  address,
  parcelId,
  stage,
  completeness,
  title,
  onClick,
}: DealHeaderCardProps) {
  const displayName = address || title || parcelId || "Active Deal";
  const ringSize = 28;
  const strokeWidth = 3;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (completeness / 100) * circumference;

  return (
    <div
      className={`deal-header ${onClick ? "deal-header--clickable" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      <div className="deal-header-left">
        <span className="deal-header-address">{displayName}</span>
        <DealStageBadge stage={stage} />
      </div>
      <div className="deal-header-right">
        <svg
          width={ringSize}
          height={ringSize}
          viewBox={`0 0 ${ringSize} ${ringSize}`}
          className="dsr-ring-mini"
        >
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke={completeness >= 75 ? "var(--success)" : completeness >= 50 ? "var(--warning)" : "var(--accent)"}
            strokeWidth={strokeWidth}
            strokeDasharray={`${filled} ${circumference}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
          />
        </svg>
        <span className="deal-header-pct">{completeness}%</span>
        {onClick && (
          <span className="deal-header-chevron">
            <ChevronRight />
          </span>
        )}
      </div>
    </div>
  );
}
